import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Inbox,
  Calendar,
  CheckSquare,
  FolderKanban,
  Tags,
  Users,
  LayoutGrid,
  StickyNote,
  Search,
  Clock,
  PanelLeftClose,
  PanelLeft,
  Settings,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';

interface RecentItem {
  path: string;
  title: string;
  type: 'task' | 'note' | 'meeting' | 'project' | 'view' | 'meeting-series';
  visitedAt: number;
}

const RECENT_ICONS: Record<RecentItem['type'], LucideIcon> = {
  task: CheckSquare,
  note: StickyNote,
  meeting: Calendar,
  'meeting-series': Calendar,
  project: FolderKanban,
  view: LayoutGrid,
};

const STORAGE_KEY = 'dossier-recent-items';
const MAX_RECENT = 5;

function getRecentItems(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentItems(items: RecentItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

/** Match a route to a trackable entity type, or null if not trackable. */
function matchRoute(pathname: string): { type: RecentItem['type']; idParam: string } | null {
  const patterns: [RegExp, RecentItem['type']][] = [
    [/^\/tasks\/([^/]+)$/, 'task'],
    [/^\/notes\/([^/]+)$/, 'note'],
    [/^\/meetings\/standalone\/([^/]+)$/, 'meeting'],
    [/^\/meetings\/[^/]+\/([^/]+)$/, 'meeting'],
    [/^\/meeting-instances\/([^/]+)$/, 'meeting'],
    [/^\/meetings\/([^/]+)$/, 'meeting-series'],
    [/^\/projects\/([^/]+)$/, 'project'],
    [/^\/views\/([^/]+)$/, 'view'],
  ];
  for (const [re, type] of patterns) {
    const m = pathname.match(re);
    if (m) return { type, idParam: m[1] };
  }
  return null;
}

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/meetings', icon: Calendar, label: 'Meetings' },
  { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
  { to: '/notes', icon: StickyNote, label: 'Notes' },
  { to: '/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/tags', icon: Tags, label: 'Tags' },
  { to: '/people', icon: Users, label: 'People' },
  { to: '/views', icon: LayoutGrid, label: 'Views' },
  { to: '/search', icon: Search, label: 'Search' },
  { to: '/timeline', icon: Clock, label: 'Timeline' },
];

export function Sidebar({ open, onToggle }: SidebarProps) {
  const location = useLocation();
  const { prefs } = usePreferences();
  const showRecent = prefs['sidebar.recentItems'] !== 'false';
  const recentCount = parseInt(prefs['sidebar.recentItemsCount'] || '5', 10) || MAX_RECENT;
  const [recentItems, setRecentItems] = useState<RecentItem[]>(getRecentItems);

  // Track page title changes to capture the entity name
  const updateRecentTitle = useCallback((path: string, title: string) => {
    setRecentItems(prev => {
      const updated = prev.map(item => item.path === path ? { ...item, title } : item);
      saveRecentItems(updated);
      return updated;
    });
  }, []);

  // Track navigation to entity detail pages
  useEffect(() => {
    const match = matchRoute(location.pathname);
    if (!match) return;

    setRecentItems(prev => {
      // If we already have this path with a title, just move it to top
      const existing = prev.find(item => item.path === location.pathname);
      const filtered = prev.filter(item => item.path !== location.pathname);
      const newItem: RecentItem = {
        path: location.pathname,
        title: existing?.title || '',
        type: match.type,
        visitedAt: Date.now(),
      };
      const updated = [newItem, ...filtered].slice(0, MAX_RECENT);
      saveRecentItems(updated);
      return updated;
    });

    // Watch for the page heading to appear and grab the entity title
    const observer = new MutationObserver(() => {
      const h1 = document.querySelector('h1');
      if (h1) {
        const title = h1.textContent?.trim();
        if (title) {
          updateRecentTitle(location.pathname, title);
          observer.disconnect();
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Also check immediately in case h1 is already rendered
    const h1 = document.querySelector('h1');
    if (h1?.textContent?.trim()) {
      updateRecentTitle(location.pathname, h1.textContent.trim());
      observer.disconnect();
    }

    // Cleanup after 3s max
    const timer = setTimeout(() => observer.disconnect(), 3000);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [location.pathname, updateRecentTitle]);

  return (
    <aside
      className={`bg-bg-secondary border-r border-border flex flex-col transition-all duration-200
        fixed inset-y-0 left-0 z-40 sm:relative sm:z-auto
        ${open ? 'w-[260px]' : 'w-0 overflow-hidden'
      }`}
    >
      <div className="flex items-center justify-between h-[52px] px-4 border-b border-border">
        <span className="text-lg font-semibold text-text-primary tracking-tight">
          Dossier
        </span>
        <button
          onClick={onToggle}
          className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
        >
          {open ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
        </button>
      </div>

      <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
              }`
            }
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Recent Items */}
      {showRecent && recentItems.length > 0 && (
        <div className="border-t border-border px-2 py-2">
          <span className="px-3 text-[10px] font-medium uppercase tracking-wider text-text-muted">
            Recent
          </span>
          <div className="mt-1 space-y-0.5">
            {recentItems.slice(0, recentCount).map(item => {
              const Icon = RECENT_ICONS[item.type];
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                      isActive
                        ? 'bg-accent/10 text-accent'
                        : 'text-text-muted hover:text-text-secondary hover:bg-bg-hover'
                    }`
                  }
                >
                  <Icon size={14} className="flex-shrink-0" />
                  <span className="truncate">{item.title || 'Untitled'}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      )}

      <div className="border-t border-border px-2 py-2 flex items-center justify-between">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-colors ${
              isActive
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:text-text-secondary hover:bg-bg-hover'
            }`
          }
        >
          <Settings size={14} />
          <span>Settings</span>
        </NavLink>
        <span className="text-[10px] text-text-muted px-2">v{__APP_VERSION__}</span>
      </div>
    </aside>
  );
}
